/**
 * 16-bit PCM WAV encoder (W1-14). Pure: no Web Audio, runs in the audio worker, on the main thread and in Node.
 *
 * Layout (RIFF/WAVE, canonical 44-byte header, little endian):
 *   0 'RIFF' | 4 u32 36 + dataBytes | 8 'WAVE'
 *  12 'fmt ' | 16 u32 16 | 20 u16 1 (PCM) | 22 u16 channels | 24 u32 sampleRate | 28 u32 byteRate
 *  32 u16 blockAlign | 34 u16 bitsPerSample (16)
 *  36 'data' | 40 u32 dataBytes | 44 interleaved frames (ch0, ch1, ... per frame)
 *
 * Quantisation: NaN -> 0, clamp to [-1, 1], negative samples scale by 32768 and positive ones by 32767, rounded to
 * the nearest integer, so -1 -> -32768, 1 -> 32767 and 0 -> 0 exactly. No dither (output is deterministic).
 * Used by the wizard's codec conversion (Opus/M4A -> WAV through the audio worker) and the synthesised sample song.
 */

import type { EncodeWavFn } from './types';

/** Size of the canonical header written before the sample data. */
export const WAV_HEADER_BYTES = 44;

/** RIFF sizes are u32: the whole file (minus the 8-byte RIFF preamble) must fit. */
const MAX_RIFF_PAYLOAD = 0xffffffff;

const LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}

/** Float sample -> signed 16-bit integer (see the module header for the exact rule). */
export function quantize16(x: number): number {
  if (Number.isNaN(x)) return 0;
  if (x <= -1) return -32768;
  if (x >= 1) return 32767;
  // `| 0` also turns -0 into 0.
  return (x < 0 ? Math.round(x * 32768) : Math.round(x * 32767)) | 0;
}

export const encodeWav: EncodeWavFn = (channels, sampleRate, bits) => {
  if ((bits as number) !== 16) throw new RangeError(`encodeWav: only 16-bit PCM is supported (got ${String(bits)})`);
  const channelCount = channels.length;
  const first = channels[0];
  if (first === undefined) throw new RangeError('encodeWav: at least one channel is required');
  if (channelCount > 0xffff) throw new RangeError(`encodeWav: too many channels (${channelCount})`);
  const rate = Math.round(sampleRate);
  if (!Number.isFinite(sampleRate) || rate < 1 || rate > 0xffffffff) {
    throw new RangeError(`encodeWav: invalid sample rate ${String(sampleRate)}`);
  }
  const frames = first.length;
  for (let c = 1; c < channelCount; c++) {
    const ch = channels[c];
    if (ch?.length !== frames) {
      throw new RangeError(`encodeWav: channel ${c} has ${String(ch?.length)} samples, expected ${frames}`);
    }
  }
  const blockAlign = channelCount * 2;
  const dataBytes = frames * blockAlign;
  if (36 + dataBytes > MAX_RIFF_PAYLOAD) throw new RangeError('encodeWav: audio too long for a RIFF file');
  const byteRate = rate * blockAlign;
  if (byteRate > 0xffffffff) throw new RangeError('encodeWav: byte rate does not fit the WAV header');

  const out = new Uint8Array(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(out.buffer);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);

  if (LITTLE_ENDIAN) {
    // Fast path: the header is 44 bytes (even), so the data is 2-byte aligned for an Int16Array view.
    const pcm = new Int16Array(out.buffer, WAV_HEADER_BYTES, frames * channelCount);
    for (let c = 0; c < channelCount; c++) {
      const src = channels[c] ?? first;
      for (let i = 0, j = c; i < frames; i++, j += channelCount) pcm[j] = quantize16(src[i] ?? 0);
    }
  } else {
    for (let c = 0; c < channelCount; c++) {
      const src = channels[c] ?? first;
      for (let i = 0; i < frames; i++) {
        view.setInt16(WAV_HEADER_BYTES + i * blockAlign + c * 2, quantize16(src[i] ?? 0), true);
      }
    }
  }
  return out;
};
